import { useEffect } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { captureMessage } from "src/lib/monitoring";
import { useResourcePermission } from "./use-resource-permission";

export enum AIDRole {
  AidViewer = "aid-viewer", // view any aid-related resource, but cannot create, edit or delete
  AidContentEditor = "aid-content-editor", // modify message tags, variables and collections, but cannot modify anything else
  AidAdmin = "aid-admin", // can do anything aid-related
}

const ROLE_RANK: Record<AIDRole, number> = {
  [AIDRole.AidViewer]: 0,
  [AIDRole.AidContentEditor]: 1,
  [AIDRole.AidAdmin]: 2,
};

const isAidRole = (role: string): role is AIDRole => {
  return Object.values(AIDRole).includes(role as AIDRole);
};

const loggedMismatches = new Set<string>();

// Dual-read during the LD → permissions v2 migration: a user is permitted if
// EITHER the legacy LaunchDarkly role (appAidPermissionsUserRoles) OR the
// workspace-level decision engine grants allow the action, so grants can roll
// out without anyone losing access. Once all LD targeting is translated to
// grants, the LD path (and this hook) goes away in favor of
// useResourcePermission at the call sites.
export const useAIDPermissions = ({
  minRequiredRole,
}: {
  minRequiredRole: AIDRole;
}): { isPermitted: boolean; isLoading: boolean } => {
  const { appAidPermissionsUserRoles: userRole } = useFlags();

  const { isPermitted: isWorkspaceAdmin, isLoading: isWorkspaceAdminLoading } =
    useResourcePermission({
      v2: { resource: "workspace", grant: "can_update" },
    });
  const { isPermitted: canManage, isLoading: canManageLoading } =
    useResourcePermission({
      v2: { resource: "workspace", grant: "can_manage_decision_engine" },
    });
  const { isPermitted: canEditContent, isLoading: canEditContentLoading } =
    useResourcePermission({
      v2: { resource: "workspace", grant: "can_edit_decision_engine_content" },
    });

  // Viewing (rank 0) is implied by workspace membership; the manage grant
  // also satisfies content-editor checks (admin ⊇ content editor).
  const grantRank = canManage ? 2 : canEditContent ? 1 : 0;
  const ldRank = isAidRole(userRole) ? ROLE_RANK[userRole] : 0;
  const requiredRank = ROLE_RANK[minRequiredRole];

  // Migration telemetry, removed with the LD path: an LD role and the v2
  // grants disagreeing on a translated workspace means the translation is
  // wrong — bake after translating, then drop the LD targeting.
  const mismatch =
    isAidRole(userRole) &&
    !isWorkspaceAdmin &&
    grantRank >= requiredRank !== ldRank >= requiredRank;
  useEffect(() => {
    if (!mismatch) return;
    const signature = `${userRole}|${grantRank}|${requiredRank}`;
    if (loggedMismatches.has(signature)) return;
    loggedMismatches.add(signature);
    captureMessage(
      `AID permission mismatch: LD role ${userRole} vs grant rank ${grantRank} for required rank ${requiredRank}`,
    );
  }, [mismatch, userRole, grantRank, requiredRank]);

  return {
    isPermitted:
      isWorkspaceAdmin || Math.max(grantRank, ldRank) >= requiredRank,
    isLoading:
      isWorkspaceAdminLoading || canManageLoading || canEditContentLoading,
  };
};

type AidAction = "create" | "update" | "delete" | "run";
type AidResource =
  | "agent"
  | "message"
  | "content"
  | "outcome"
  | "configuration"
  | "channel"
  | "collection"
  | "offer"
  | "send event";

export const aidPermissionTooltip = (
  isPermitted: boolean,
  action: AidAction,
  resource: AidResource,
) => {
  if (isPermitted) {
    return undefined;
  }

  const article = getArticle(action, resource);

  return `You do not have permission to ${action} ${article} ${resource}`;
};

const getArticle = (action: AidAction, resource: AidResource) => {
  if (action !== "create") return "this";

  switch (resource) {
    case "agent":
    case "outcome":
    case "offer":
      return "an";
    case "configuration":
    case "channel":
    case "collection":
    case "message":
    case "send event":
      return "a";
    case "content":
      return "new";
  }
};
